// apps/client/src/pages/leads/website/WebsiteConnectionEditor.tsx
//
// Create / edit form for one website connection, shown in a modal.
// The tenant's super_admin only (the page never opens it for read-only
// roles; the callable refuses anyone else, founders included, regardless).

import React, { useMemo, useState } from 'react';
import { AlertTriangle, Plus, Save, Trash2 } from 'lucide-react';
import {
  PROTECTION_MODES,
  type ProtectionMode,
  type WebsiteConnectionInput,
} from '@valutten/shared-schemas/dist/websiteConnection';
import { resolveClassificationOptions } from '@valutten/shared-schemas/dist/leadClassification';
import type { ClassificationOptions } from '@valutten/shared-schemas';
import Modal from '../../../components/common/Modal';
import { Spinner } from '../../../components/common/Spinner';
import { LeadClassificationFields } from '../../../components/leads/LeadClassificationFields';
import type {
  WebsiteConnectionSaveInput,
  WebsiteConnectionView,
  WebsiteReferralPartnerOption,
} from '../../../hooks/useWebsiteConnections';
import {
  ENQUIRY_PRESETS,
  MODE_LABELS,
  blankVariant,
  emptyFormState,
  formStateFromConnection,
  presetFromClassification,
  validateForm,
  variantFromPreset,
  type EnquiryPresetId,
  type EnquiryVariantFormState,
  type WebsiteConnectionFormErrors,
  type WebsiteConnectionFormState,
} from './websiteConnectionForm';
import { WebsiteRoutingPreview, type WebsiteRoutingContext } from './WebsiteRoutingPreview';

export interface SelectOption {
  id: string;
  label: string;
}

interface Props {
  show: boolean;
  /** Null = create. */
  connection: WebsiteConnectionView | null;
  serviceCategories: SelectOption[];
  offices: SelectOption[];
  /** Null = the server returned no partner list. */
  referralPartners: WebsiteReferralPartnerOption[] | null;
  /** The tenant's classification options per service (getLeadTaxonomy); null = defaults only. */
  classification?: Record<string, ClassificationOptions> | null;
  /** Routing rules + routing_v2 mode for the "where do leads go" preview. */
  routing?: WebsiteRoutingContext | null;
  onClose: () => void;
  /** Create receives no `canary` (stored off); update always carries it. */
  onSave: (input: WebsiteConnectionInput | WebsiteConnectionSaveInput) => Promise<void>;
}

const inputClass = 'w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-sm';
const labelClass = 'block text-xs font-semibold text-gray-400 mb-1';

/** The list plus a stored value it no longer offers, so saving never silently drops it. */
function withStored(list: SelectOption[], id: string, label: (id: string) => string): SelectOption[] {
  if (!id || list.some((o) => o.id === id)) return list;
  return [...list, { id, label: label(id) }];
}

const ServiceOfficeSelects: React.FC<{
  idPrefix: string;
  serviceCategory: string;
  officeId: string;
  serviceCategories: SelectOption[];
  offices: SelectOption[];
  onService: (v: string) => void;
  onOffice: (v: string) => void;
}> = ({ idPrefix, serviceCategory, officeId, serviceCategories, offices, onService, onOffice }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div>
      <label htmlFor={`${idPrefix}-category`} className={labelClass}>Service category</label>
      <select id={`${idPrefix}-category`} value={serviceCategory}
        onChange={(e) => onService(e.target.value)} className={inputClass}>
        <option value="">Not fixed</option>
        {withStored(serviceCategories, serviceCategory, (id) => id).map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
      </select>
    </div>
    <div>
      <label htmlFor={`${idPrefix}-office`} className={labelClass}>Office</label>
      <select id={`${idPrefix}-office`} value={officeId}
        onChange={(e) => onOffice(e.target.value)} className={inputClass}>
        <option value="">Company-wide (routing decides)</option>
        {withStored(offices, officeId, (id) => `Office ${id}`).map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </div>
  </div>
);

export const WebsiteConnectionEditor: React.FC<Props> = ({
  show,
  connection,
  serviceCategories,
  offices,
  referralPartners,
  classification = null,
  routing = null,
  onClose,
  onSave,
}) => {
  const [state, setState] = useState<WebsiteConnectionFormState>(() =>
    connection ? formStateFromConnection(connection) : emptyFormState(),
  );
  const [errors, setErrors] = useState<WebsiteConnectionFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof WebsiteConnectionFormState>(key: K, value: WebsiteConnectionFormState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  // A stored partner that is no longer offered (inactive, or no longer
  // operated by this company) stays visible so saving never silently drops it.
  const partnerOptions = useMemo(() => {
    const list = (referralPartners ?? []).map((p) => ({ id: p.partnerId, label: p.name }));
    if (state.referralPartnerId && !list.some((p) => p.id === state.referralPartnerId)) {
      list.unshift({ id: state.referralPartnerId, label: `Partner ${state.referralPartnerId} (not available)` });
    }
    return list;
  }, [referralPartners, state.referralPartnerId]);

  // ── Named enquiry forms (variants) ──
  const [presetChoice, setPresetChoice] = useState<EnquiryPresetId | ''>('');
  const takenKeys = state.enquiryVariants.map((v) => v.key.trim());
  const updateVariant = (i: number, patch: Partial<EnquiryVariantFormState>) =>
    setState((s) => ({
      ...s,
      enquiryVariants: s.enquiryVariants.map((v, j) => (j === i ? { ...v, ...patch } : v)),
    }));
  const addVariant = (v: EnquiryVariantFormState) =>
    setState((s) => ({ ...s, enquiryVariants: [...s.enquiryVariants, v] }));
  const removeVariant = (i: number) =>
    setState((s) => ({ ...s, enquiryVariants: s.enquiryVariants.filter((_, j) => j !== i) }));

  const handleSave = async () => {
    setSaveError(null);
    const v = validateForm(state);
    if (!v.ok) {
      setErrors(v.errors);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await onSave(connection ? { ...v.input, canary: { enabled: state.canaryEnabled } } : v.input);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'The connection was not saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} title={connection ? `Edit ${connection.label}` : 'Connect a website'} onClose={onClose}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="wc-label" className={labelClass}>Name</label>
            <input id="wc-label" type="text" maxLength={80} value={state.label}
              onChange={(e) => set('label', e.target.value)} className={inputClass} placeholder="Main website" />
            {errors.label && <p className="text-xs text-red-400 mt-1">{errors.label}</p>}
          </div>
          <div>
            <label htmlFor="wc-env" className={labelClass}>Environment</label>
            <select id="wc-env" value={state.environment}
              onChange={(e) => set('environment', e.target.value === 'staging' ? 'staging' : 'production')}
              className={inputClass}>
              <option value="production">Live website</option>
              <option value="staging">Test website</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="wc-origins" className={labelClass}>Website addresses allowed to submit</label>
          <textarea id="wc-origins" rows={3} value={state.originsText}
            onChange={(e) => set('originsText', e.target.value)}
            className={`${inputClass} font-mono`} placeholder={'https://www.example.com.au\nhttps://example.com.au'} />
          <p className="text-[0.6875rem] text-gray-500 mt-1">
            One per line, https only, the site address without any page path. List www and non-www separately.
          </p>
          {errors.origins?.map((m) => (
            <p key={m} className="text-xs text-red-400 mt-1">{m}</p>
          ))}
        </div>

        <fieldset>
          <legend className={labelClass}>Protection</legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {PROTECTION_MODES.map((m: ProtectionMode) => (
              <label key={m}
                className={`rounded-md border p-3 cursor-pointer text-sm ${state.mode === m ? 'border-blue-500/60 bg-blue-500/10' : 'border-gray-700 hover:bg-gray-800/60'}`}>
                <input type="radio" name="wc-mode" value={m} checked={state.mode === m}
                  onChange={() => set('mode', m)} className="mr-2" />
                <span className="font-medium">{MODE_LABELS[m].label}</span>
                <span className="block text-xs text-gray-400 mt-1">{MODE_LABELS[m].help}</span>
              </label>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <label htmlFor="wc-minscore" className="text-xs text-gray-400">Minimum bot score</label>
            <input id="wc-minscore" type="range" min={0} max={1} step={0.1} value={state.minScore}
              onChange={(e) => set('minScore', Number(e.target.value))} className="flex-1" />
            <span className="text-sm font-mono w-10 text-right">{state.minScore.toFixed(1)}</span>
          </div>
          <p className="text-[0.6875rem] text-gray-500 mt-1">0.5 suits most sites. Higher refuses more, including some real people.</p>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className={labelClass}>Forms</legend>
          {errors.forms && <p className="text-xs text-red-400">{errors.forms}</p>}

          <div className="rounded-md border border-gray-700 p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={state.enquiryEnabled} onChange={(e) => set('enquiryEnabled', e.target.checked)} />
              Enquiry form (creates a lead)
            </label>
            {state.enquiryEnabled && (
              <>
                <ServiceOfficeSelects
                  idPrefix="wc"
                  serviceCategory={state.enquiryServiceCategory}
                  officeId={state.enquiryOfficeId}
                  serviceCategories={serviceCategories}
                  offices={offices}
                  onService={(v) => set('enquiryServiceCategory', v)}
                  onOffice={(v) => set('enquiryOfficeId', v)}
                />
                <WebsiteRoutingPreview
                  context={routing}
                  testId="wc-routing-enquiry"
                  target={{ serviceCategory: state.enquiryServiceCategory || null, officeId: state.enquiryOfficeId || null }}
                />

                <div className="border-t border-gray-700 pt-3 space-y-3" data-testid="wc-variants">
                  <div>
                    <p className="text-xs font-semibold text-gray-400">Named enquiry forms</p>
                    <p className="text-[0.6875rem] text-gray-500 mt-0.5">
                      For a site with several enquiry forms (home loan, commercial and so on). Each has its own service,
                      office and preset answers. The settings above apply to any form the website sends without a name.
                    </p>
                  </div>

                  {state.enquiryVariants.map((v, i) => {
                    const options = resolveClassificationOptions(classification ?? undefined, v.serviceCategory || null);
                    const err = errors.variants?.[i];
                    return (
                      <div key={i} className="rounded-md border border-gray-700 bg-gray-900/30 p-3 space-y-3" data-testid={`wc-variant-${i}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label htmlFor={`wc-variant-${i}-label`} className={labelClass}>Form name</label>
                            <input id={`wc-variant-${i}-label`} type="text" maxLength={80} value={v.label}
                              onChange={(e) => updateVariant(i, { label: e.target.value })}
                              className={inputClass} placeholder="Home loan enquiry" />
                          </div>
                          <div>
                            <label htmlFor={`wc-variant-${i}-key`} className={labelClass}>Form key</label>
                            <input id={`wc-variant-${i}-key`} type="text" maxLength={40} value={v.key}
                              onChange={(e) => updateVariant(i, { key: e.target.value.toLowerCase() })}
                              className={`${inputClass} font-mono`} />
                            <p className="text-[0.6875rem] text-gray-500 mt-1">
                              Your website sends this as <code className="text-gray-300">variant: &quot;{v.key.trim() || 'key'}&quot;</code> with the enquiry.
                            </p>
                          </div>
                        </div>
                        <ServiceOfficeSelects
                          idPrefix={`wc-variant-${i}`}
                          serviceCategory={v.serviceCategory}
                          officeId={v.officeId}
                          serviceCategories={serviceCategories}
                          offices={offices}
                          onService={(val) => updateVariant(i, { serviceCategory: val })}
                          onOffice={(val) => updateVariant(i, { officeId: val })}
                        />
                        {options && (
                          <div>
                            <p className="text-xs font-semibold text-gray-400 mb-1">Preset answers</p>
                            <p className="text-[0.6875rem] text-gray-500 mb-2">
                              Used only where the visitor does not answer the question themselves.
                            </p>
                            <LeadClassificationFields
                              options={options}
                              value={v.preset}
                              onChange={(next) => updateVariant(i, { preset: next })}
                              idPrefix={`wc-variant-${i}-preset`}
                            />
                          </div>
                        )}
                        <WebsiteRoutingPreview
                          context={routing}
                          testId={`wc-routing-variant-${i}`}
                          target={{
                            serviceCategory: v.serviceCategory || null,
                            officeId: v.officeId || null,
                            preset: presetFromClassification(v.preset),
                          }}
                        />
                        {err && <p className="text-xs text-red-400">{err}</p>}
                        <div className="flex justify-end">
                          <button type="button" onClick={() => removeVariant(i)}
                            className="text-xs text-gray-400 hover:text-red-300 inline-flex items-center gap-1"
                            aria-label={`Remove ${v.label.trim() || 'this form'}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex flex-wrap items-center gap-2">
                    <label htmlFor="wc-variant-preset" className="sr-only">Add from preset</label>
                    <select id="wc-variant-preset" value={presetChoice}
                      onChange={(e) => setPresetChoice(e.target.value as EnquiryPresetId | '')}
                      className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-xs">
                      <option value="">Choose a preset</option>
                      {ENQUIRY_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                    <button type="button" disabled={!presetChoice}
                      onClick={() => {
                        if (!presetChoice) return;
                        addVariant(variantFromPreset(presetChoice, { serviceCategories, classification, takenKeys }));
                        setPresetChoice('');
                      }}
                      className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-xs inline-flex items-center gap-1 disabled:opacity-50">
                      <Plus className="w-3.5 h-3.5" />
                      Add from preset
                    </button>
                    <button type="button" onClick={() => addVariant(blankVariant(takenKeys))}
                      className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-xs inline-flex items-center gap-1">
                      <Plus className="w-3.5 h-3.5" />
                      Add a blank form
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="rounded-md border border-gray-700 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={state.newsletterEnabled} onChange={(e) => set('newsletterEnabled', e.target.checked)} />
              Newsletter form (adds an eDM subscriber)
            </label>
            {state.newsletterEnabled && (
              <label className="flex items-center gap-2 text-sm text-gray-300 ml-6">
                <input type="checkbox" checked={state.welcomeEmail} onChange={(e) => set('welcomeEmail', e.target.checked)} />
                Send a welcome email to new subscribers
              </label>
            )}
          </div>

          <div className="rounded-md border border-gray-700 p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={state.referralEnabled} onChange={(e) => set('referralEnabled', e.target.checked)} />
              Referral form (creates an unverified Partner Network referral)
            </label>
            {state.referralEnabled && (
              <div>
                <label htmlFor="wc-partner" className={labelClass}>Partner that receives referrals</label>
                {referralPartners === null && partnerOptions.length === 0 ? (
                  <p className="text-xs text-amber-300">
                    The partner list could not be loaded. Close this form and refresh the page.
                  </p>
                ) : partnerOptions.length === 0 ? (
                  <p className="text-xs text-amber-300">
                    Your company does not operate a Partner Network partner yet, so the referral form has nowhere to send referrals.
                  </p>
                ) : (
                  <select id="wc-partner" value={state.referralPartnerId}
                    onChange={(e) => set('referralPartnerId', e.target.value)} className={inputClass}>
                    <option value="">Choose a partner</option>
                    {partnerOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                )}
                {errors.referralPartner && <p className="text-xs text-red-400 mt-1">{errors.referralPartner}</p>}
                <label className="flex items-center gap-2 text-sm text-gray-300 mt-3">
                  <input type="checkbox" checked={state.referralRouteAsLead}
                    onChange={(e) => set('referralRouteAsLead', e.target.checked)} />
                  Also create a lead and route it to a broker
                </label>
                {state.referralRouteAsLead && (
                  <div className="ml-6 mt-2 space-y-2">
                    <ServiceOfficeSelects
                      idPrefix="wc-referral"
                      serviceCategory={state.referralServiceCategory}
                      officeId={state.referralOfficeId}
                      serviceCategories={serviceCategories}
                      offices={offices}
                      onService={(v) => set('referralServiceCategory', v)}
                      onOffice={(v) => set('referralOfficeId', v)}
                    />
                    <WebsiteRoutingPreview
                      context={routing}
                      testId="wc-routing-referral"
                      target={{ serviceCategory: state.referralServiceCategory || null, officeId: state.referralOfficeId || null }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </fieldset>

        {connection && (
          <div className="rounded-md border border-gray-700 p-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={state.canaryEnabled} onChange={(e) => set('canaryEnabled', e.target.checked)} />
              Daily test submission
            </label>
            <p className="text-[0.6875rem] text-gray-500 mt-1 ml-6">
              Once a day VALUTTEN sends a marked test through each form that is on to confirm it still works; test entries are labelled and cleaned up.
            </p>
          </div>
        )}

        {saveError && (
          <div role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300 flex gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{saveError}</span>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-sm">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Spinner size="sm" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving' : connection ? 'Save changes' : 'Create connection'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default WebsiteConnectionEditor;
